import { RouteConfig } from 'vue-router'
import bookTagRoute from '@/router/routes/bookTag'
import lazyImporter from '@/utils/lazyImporter'

const getContentPath = (needsBookSlug: boolean = false) => {
  const prefix = needsBookSlug ? ':slug/' : ''

  return `${prefix}content`
}

const bookContentRoutes = (bookContext: string, needsBookSlug: boolean = false): RouteConfig[] => ([
  {
    component: lazyImporter('book/BookContentAdd'),
    name: `admin_${bookContext}_content_add`,
    path: `${getContentPath(needsBookSlug)}/add`,
    props: (route) => ({ bookContext, ownerSlug: route.params.slug })
  },
  {
    component: lazyImporter('book/BookContentList'),
    name: `admin_${bookContext}_content_list`,
    path: `${getContentPath(needsBookSlug)}`,
    props: (route) => ({ bookContext, ownerSlug: route.params.slug })
  },
  {
    component: lazyImporter('book/BookContentUpdate'),
    name: `admin_${bookContext}_content_update`,
    path: `${getContentPath(needsBookSlug)}/:contentSlug/update`,
    props: (route) => ({ bookContext, ownerSlug: route.params.slug, slug: route.params.contentSlug })
  }
])

const bookRoute: RouteConfig = {
  component: lazyImporter('View'),
  meta: { title: 'books', icon: 'book' },
  name: 'admin_books',
  path: '/book',
  redirect: { name: 'admin_book_list' },
  children: [
    {
      component: lazyImporter('book/BookList'),
      meta: { title: 'bookList', hidden: true },
      name: 'admin_book_list',
      path: ''
    },
    {
      component: lazyImporter('book/draft/BookDraftList'),
      meta: { title: 'draftList', hidden: true },
      name: 'admin_book_draft_list',
      path: 'draft'
    },
    {
      component: lazyImporter('book/BookAdd'),
      meta: { title: 'bookAdd', hidden: true },
      name: 'admin_book_add',
      path: 'add',
      redirect: { name: 'admin_book_add_details' },
      children: [
        ...bookContentRoutes('book_add', true),
        {
          component: lazyImporter('book/add/BookAddDetails'),
          name: 'admin_book_add_details',
          path: 'details'
        },
        {
          component: lazyImporter('book/BookAuthors'),
          name: 'admin_book_add_authors',
          path: ':slug/authors',
          props: true
        },
        {
          component: lazyImporter('book/BookValidate'),
          name: 'admin_book_add_validate',
          path: ':slug/validate',
          props: true
        }
      ]
    },
    {
      component: lazyImporter('book/BookUpdate'),
      meta: { title: 'bookUpdate', hidden: true },
      name: 'admin_book_update',
      path: ':slug/update',
      props: true,
      redirect: (to) => {
        const { params } = to

        if (params.slug) {
          return `/book/${params.slug}/update/details`
        }

        return { name: 'admin_book_list' }
      },
      children: [
        ...bookContentRoutes('book_update'),
        {
          component: lazyImporter('book/update/BookUpdateDetails'),
          name: 'admin_book_update_details',
          path: 'details',
          props: true
        },
        {
          component: lazyImporter('book/BookAuthors'),
          name: 'admin_book_update_authors',
          path: 'authors',
          props: true
        }
      ]
    },
    {
      component: lazyImporter('book/draft/BookDraftUpdate'),
      meta: { title: 'bookDraftUpdate', hidden: true },
      name: 'admin_book_draft_update',
      path: 'draft/:slug/update',
      props: true,
      redirect: (to) => {
        const { params } = to

        if (params.slug) {
          return `/book/draft/${params.slug}/update/details`
        }

        return { name: 'admin_book_list' }
      },
      children: [
        ...bookContentRoutes('book_draft_update'),
        {
          component: lazyImporter('book/update/BookUpdateDetails'),
          name: 'admin_book_draft_update_details',
          path: 'details',
          props: (route) => {
            return { slug: route.params.slug, isDraft: true }
          }
        },
        {
          component: lazyImporter('book/BookAuthors'),
          name: 'admin_book_draft_update_authors',
          path: 'authors',
          props: true
        },
        {
          component: lazyImporter('book/BookValidate'),
          name: 'admin_book_draft_update_validate',
          path: 'validate',
          props: true
        }
      ]
    },
    bookTagRoute
  ]
}

export default bookRoute
